/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { HelpRequestType } from '@gh/builder-shared-data';
import { UserContext } from '@gh/common-shared';
import { isServerErrorStatus } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';
import { tap$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';
import { helpBugRequestDialog } from '../components';
import { HelpBugRequestDialogConfig } from '../resources';

@Injectable()
export class HelpHttpErrorInterceptor implements HttpInterceptor {
  private helpDialogShown: boolean = false;

  constructor(private injector: Injector,
              private userContext: UserContext) {

  }

  get dialogService(): DialogService {
    return this.injector.get(DialogService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap$({
        error: (response?: HttpErrorResponse) => {
          // show bug request dialog only if user context is initialized,
          // bug request dialog needs information from user context
          if (!req.url.includes('maintenance-status')) {
            if (response && (response.status === 0 || isServerErrorStatus(response.status)) && this.userContext.isInitialized()) {
              if (!this.helpDialogShown) {
                const dialogRef = this.dialogService.openWithInput(
                  helpBugRequestDialog,
                  <HelpBugRequestDialogConfig>{
                    mode: HelpRequestType.SystemBug,
                    stacktrace: this.stacktraceForBugAttachment(req, response),
                  },
                );

                dialogRef.afterOpened()
                  .subscribe(() => {
                    this.helpDialogShown = true;
                  });

                dialogRef.afterClosed()
                  .subscribe(() => {
                    this.helpDialogShown = false;
                  });
              }
            }
          }
        },
      }));
  }

  private stacktraceForBugAttachment(req: HttpRequest<any>, response: HttpErrorResponse): string | undefined {
    if (response.status === 0) {
      return JSON.stringify({
        kind: 'transport_failure',
        client: this.clientReportMetadata(),
        request: { method: req.method, url: req.url },
        response: {
          status: response.status,
          statusText: response.statusText,
          message: response.message,
        },
        error: this.serializeTransportErrorDetail(response.error),
      });
    }
    return response.error != null ? JSON.stringify(response.error) : undefined;
  }

  private clientReportMetadata(): { userLogin: string; reportedAtUtc: string } {
    const login = this.userContext.data?.login ?? '';
    return {
      userLogin: login,
      reportedAtUtc: new Date().toISOString(),
    };
  }

  private serializeTransportErrorDetail(error: unknown): unknown {
    if (error == null) {
      return null;
    }
    if (typeof error === 'string') {
      return error.length > 2000 ? `${error.slice(0, 2000)}…` : error;
    }
    if (typeof error !== 'object') {
      return String(error);
    }
    let jsonStringify: string;
    try {
      jsonStringify = JSON.stringify(error);
    } catch {
      return { note: 'non-serializable-error-object' };
    }
    const e = error as Record<string, unknown>;
    return {
      type: e.type,
      isTrusted: e.isTrusted,
      loaded: e.loaded,
      total: e.total,
      lengthComputable: e.lengthComputable,
      message: e.message,
      jsonStringify: jsonStringify.length > 2000 ? `${jsonStringify.slice(0, 2000)}…` : jsonStringify,
    };
  }
}
